import React, { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { type FieldValues } from 'react-hook-form/dist/types';

import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

import Button from '../../common/Button';
import { type TypeMember } from '../../common/dataTypes';
import { useGetOrgMembers } from '../../hooks/tanstack/useOrganizationHook';
import { useGetUser } from '../../hooks/tanstack/useUserHook';
import { useGetRouteParams } from '../../hooks/tanstack/useUtilsHook';
import FormAutoComplete from '../form/FormAutoComplete';

type TransferOwnershipDialogProps = {
  open: boolean;
  handleCancelDialog: () => void;
  handleSubmitDialog: (member: TypeMember) => void;
};

const TransferOwnershipDialog = ({
  open,
  handleCancelDialog,
  handleSubmitDialog,
}: TransferOwnershipDialogProps) => {
  const form = useForm();

  const { reset, formState, handleSubmit } = form;

  const { errors, isSubmitting } = formState;

  const { orgId } = useGetRouteParams();

  const { data: currentUser } = useGetUser();

  const { data: orgMembers } = useGetOrgMembers(
    {
      orgId,
    },
    {
      enabled: !!orgId,
    }
  );

  const validMembers = useMemo(
    () =>
      orgMembers?.filter(
        (orgMember) =>
          currentUser &&
          orgMember.user.id !== currentUser.id &&
          orgMember.role === 'organization_admin'
      ),
    [orgMembers, currentUser]
  );

  const handleCloseDialog = () => {
    reset();
    handleCancelDialog();
  };

  const onSubmit = (data: FieldValues) => {
    const { user } = data;

    const selectedUser = orgMembers?.find((o) => o.user.id === Number(user));

    if (selectedUser) {
      handleSubmitDialog(selectedUser);
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
      <DialogTitle className="w-full flex flex-col">
        <h2 className="pt-2 text-2xl font-bold">Transfer ownership</h2>
      </DialogTitle>
      <DialogContent>
        <div className="space-y-4">
          <DialogContentText>
            By transferring ownership, your account will be downgraded to
            'Admin' status
          </DialogContentText>
          <FormProvider {...form}>
            <form className="mb-2 space-y-6">
              <div className="flex flex-col gap-2">
                {validMembers && (
                  <FormAutoComplete
                    name="user"
                    label="Select a user to transfer"
                    options={validMembers.map((member) => ({
                      label:
                        member.user.first_name + ' ' + member.user.last_name,
                      value: member.user.id.toString(),
                    }))}
                  />
                )}
                {errors['email'] && (
                  <p className="px-2 text-sm text-primary-red">
                    {errors['email'].message as string}
                  </p>
                )}
              </div>
              <div className="flex-end flex justify-end gap-3">
                <Button
                  onClick={handleCloseDialog}
                  variant="outlined"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit((event) => onSubmit(event))}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  variant="contained_blue"
                >
                  Next
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TransferOwnershipDialog;
